import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { getStoreInfo } from '../../api/stores';
import { getMenus, createMenu, updateMenu, deleteMenu } from '../../api/menus';


let nextId = 1;
const makeItem = (uuid = null, category = '', name = '', price = '', description = '', duration = '') => ({
    id: nextId++,
    uuid,
    category,
    name,
    price,
    description,
    duration,
    saving: false,
    saveError: null,
});

const formatPrice = (value) => {
    const num = Number(String(value).replace(/,/g, ''));
    if (isNaN(num) || value === '') return value;
    return num.toLocaleString('ko-KR');
};


const MenusPage = () => {
    const { uuid } = useParams();
    const navigate = useNavigate();

    const [스토어명, set스토어명] = useState('');
    const [로딩중, set로딩중] = useState(true);
    const [에러, set에러] = useState(null);
    const [items, setItems] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [삭제중UUID, set삭제중UUID] = useState(null);

    useEffect(() => {
        Promise.all([
            getStoreInfo(uuid),
            getMenus(uuid).catch(() => []),
        ])
            .then(([스토어, menus]) => {
                set스토어명(스토어?.name ?? '');
                setItems(
                    menus.map((m) =>
                        makeItem(m.uuid, m.category ?? '', m.name ?? '', m.price ?? '', m.description ?? '', m.duration ?? '')
                    )
                );
            })
            .catch(() => set에러('정보를 불러오지 못했습니다.'))
            .finally(() => set로딩중(false));
    }, [uuid]);

    const updateItem = (id, field, value) =>
        setItems((prev) => prev.map((it) => it.id === id ? { ...it, [field]: value } : it));

    const addBlank = () => {
        const item = makeItem();
        setItems((prev) => [...prev, item]);
        setEditingId(item.id);
    };

    const handleSave = async (item) => {
        updateItem(item.id, 'saving', true);
        updateItem(item.id, 'saveError', null);

        const body = {
            category: item.category || undefined,
            name: item.name,
            price: item.price !== '' ? Number(String(item.price).replace(/,/g, '')) : undefined,
            description: item.description || undefined,
            duration: item.duration !== '' ? Number(item.duration) : undefined,
        };

        try {
            if (item.uuid) {
                await updateMenu(uuid, item.uuid, body);
            } else {
                const result = await createMenu(uuid, body);
                setItems((prev) =>
                    prev.map((it) => it.id === item.id ? { ...it, uuid: result?.uuid ?? result?.id ?? null } : it)
                );
            }
            setEditingId(null);
        } catch {
            updateItem(item.id, 'saveError', '저장 중 오류가 발생했습니다.');
        } finally {
            updateItem(item.id, 'saving', false);
        }
    };

    const handleDelete = async (item) => {
        if (!window.confirm('이 항목을 삭제하시겠습니까?')) return;
        if (!item.uuid) {
            setItems((prev) => prev.filter((it) => it.id !== item.id));
            if (editingId === item.id) setEditingId(null);
            return;
        }
        set삭제중UUID(item.uuid);
        try {
            await deleteMenu(uuid, item.uuid);
            setItems((prev) => prev.filter((it) => it.id !== item.id));
            if (editingId === item.id) setEditingId(null);
        } catch {
            alert('삭제 중 오류가 발생했습니다.');
        } finally {
            set삭제중UUID(null);
        }
    };

    const savedCount = items.filter((it) => it.uuid).length;

    if (로딩중) return (
        <Page><Body><Inner><StatusText>불러오는 중...</StatusText></Inner></Body></Page>
    );

    if (에러) return (
        <Page><Body><Inner><StatusText $error>{에러}</StatusText></Inner></Body></Page>
    );

    return (
        <Page>
            <Body>
                <Inner>
                    <BackButton onClick={() => navigate('/stores')}>
                        ← 스토어 목록
                    </BackButton>

                    <TitleArea>
                        <PageBadge>서비스 금액 / 메뉴</PageBadge>
                        <PageTitle>{스토어명 || '스토어'}</PageTitle>
                        <PageDesc>
                            제공하는 서비스와 가격을 등록하면 AI가 고객 문의에 정확하게 안내해드립니다.<br />
                            카테고리별로 정리하면 더 체계적으로 관리할 수 있어요.
                        </PageDesc>
                    </TitleArea>

                    <TableSection>
                        {items.length > 0 && (
                            <TableHeader>
                                <ColCategory>카테고리</ColCategory>
                                <ColName>서비스/제품명</ColName>
                                <ColPrice>가격</ColPrice>
                                <ColDesc>설명</ColDesc>
                                <ColDuration>소요시간</ColDuration>
                                <ColAction />
                            </TableHeader>
                        )}

                        <ItemList>
                            {items.map((item) => {
                                const isEditing = editingId === item.id;
                                const isDeleting = 삭제중UUID === item.uuid;

                                return (
                                    <ItemCard
                                        key={item.id}
                                        $editing={isEditing}
                                        onClick={() => !isEditing && setEditingId(item.id)}
                                    >
                                        {isEditing ? (
                                            <EditForm onClick={(e) => e.stopPropagation()}>
                                                <EditGrid>
                                                    <FormField>
                                                        <FieldLabel>카테고리 <Optional>(선택)</Optional></FieldLabel>
                                                        <FieldInput
                                                            placeholder="예: 헤어, 네일, 스킨케어"
                                                            value={item.category}
                                                            onChange={(e) => updateItem(item.id, 'category', e.target.value)}
                                                            autoFocus
                                                        />
                                                    </FormField>

                                                    <FormField>
                                                        <FieldLabel>서비스/제품명 *</FieldLabel>
                                                        <FieldInput
                                                            placeholder="예: 커트, 클렌징 페이셜"
                                                            value={item.name}
                                                            onChange={(e) => updateItem(item.id, 'name', e.target.value)}
                                                        />
                                                    </FormField>

                                                    <FormField>
                                                        <FieldLabel>가격 *</FieldLabel>
                                                        <PriceInputWrap>
                                                            <FieldInput
                                                                placeholder="예: 30000"
                                                                value={item.price}
                                                                onChange={(e) => {
                                                                    const raw = e.target.value.replace(/,/g, '');
                                                                    if (/^\d*$/.test(raw)) updateItem(item.id, 'price', raw);
                                                                }}
                                                                onBlur={(e) => {
                                                                    if (item.price !== '') updateItem(item.id, 'price', formatPrice(item.price));
                                                                }}
                                                                onFocus={() => {
                                                                    updateItem(item.id, 'price', String(item.price).replace(/,/g, ''));
                                                                }}
                                                                style={{ paddingRight: '28px' }}
                                                            />
                                                            <PriceUnit>원</PriceUnit>
                                                        </PriceInputWrap>
                                                    </FormField>

                                                    <FormField>
                                                        <FieldLabel>소요시간 <Optional>(선택)</Optional></FieldLabel>
                                                        <DurationInputWrap>
                                                            <FieldInput
                                                                placeholder="예: 60"
                                                                value={item.duration}
                                                                onChange={(e) => {
                                                                    const raw = e.target.value;
                                                                    if (/^\d*$/.test(raw)) updateItem(item.id, 'duration', raw);
                                                                }}
                                                                style={{ paddingRight: '36px' }}
                                                            />
                                                            <DurationUnit>분</DurationUnit>
                                                        </DurationInputWrap>
                                                    </FormField>
                                                </EditGrid>

                                                <FormField>
                                                    <FieldLabel>설명 <Optional>(선택)</Optional></FieldLabel>
                                                    <FieldTextarea
                                                        placeholder="예: 두피 스케일링 포함, 샴푸 마무리"
                                                        value={item.description}
                                                        onChange={(e) => updateItem(item.id, 'description', e.target.value)}
                                                        rows={2}
                                                    />
                                                </FormField>

                                                {item.saveError && <SaveError>{item.saveError}</SaveError>}

                                                <SaveRow>
                                                    <CancelBtn onClick={() => {
                                                        if (!item.uuid) setItems((prev) => prev.filter((it) => it.id !== item.id));
                                                        setEditingId(null);
                                                    }}>
                                                        취소
                                                    </CancelBtn>
                                                    <SaveBtn
                                                        onClick={() => handleSave(item)}
                                                        disabled={!item.name.trim() || item.price === '' || item.saving}
                                                    >
                                                        {item.saving ? '저장 중...' : '저장'}
                                                    </SaveBtn>
                                                </SaveRow>
                                            </EditForm>
                                        ) : (
                                            <PreviewRow>
                                                <ColCategory>
                                                    {item.category
                                                        ? <CategoryBadge>{item.category}</CategoryBadge>
                                                        : <EmptyCell>—</EmptyCell>}
                                                </ColCategory>
                                                <ColName>
                                                    <ServiceName $empty={!item.name}>
                                                        {item.name || '서비스/제품명 없음'}
                                                    </ServiceName>
                                                </ColName>
                                                <ColPrice>
                                                    <PriceText $empty={item.price === ''}>
                                                        {item.price !== ''
                                                            ? `${formatPrice(item.price)}원`
                                                            : '—'}
                                                    </PriceText>
                                                </ColPrice>
                                                <ColDesc>
                                                    <DescText $empty={!item.description}>
                                                        {item.description || '—'}
                                                    </DescText>
                                                </ColDesc>
                                                <ColDuration>
                                                    <DurationText $empty={!item.duration}>
                                                        {item.duration ? `${item.duration}분` : '—'}
                                                    </DurationText>
                                                </ColDuration>
                                                <ColAction>
                                                    <DeleteBtn
                                                        onClick={(e) => { e.stopPropagation(); handleDelete(item); }}
                                                        disabled={isDeleting}
                                                        title="삭제"
                                                    >
                                                        {isDeleting ? '...' : '✕'}
                                                    </DeleteBtn>
                                                    {!item.uuid && <UnsavedDot title="미저장" />}
                                                </ColAction>
                                            </PreviewRow>
                                        )}
                                    </ItemCard>
                                );
                            })}

                            <AddButton onClick={addBlank}>+ 항목 추가하기</AddButton>
                        </ItemList>
                    </TableSection>

                    <BottomArea>
                        <CountText>
                            {savedCount > 0
                                ? <><Strong>{savedCount}개</Strong> 서비스가 등록됐어요</>
                                : '서비스를 등록하면 AI가 가격 문의에 정확하게 답변할 수 있어요'
                            }
                        </CountText>
                    </BottomArea>
                </Inner>
            </Body>
        </Page>
    );
};


/* ─────────────────────────────────────────────
   Styled Components
───────────────────────────────────────────── */
const Page = styled.div`
    width: 100%;
    min-height: 100dvh;
    background: #f7f8fa;
    font-family: Pretendard, -apple-system, sans-serif;
`;

const Body = styled.div`
    padding-top: 80px;
    padding-bottom: 60px;
    display: flex;
    justify-content: center;
`;

const Inner = styled.div`
    width: 100%;
    max-width: 860px;
    padding: 0 16px;
    display: flex;
    flex-direction: column;
    gap: 32px;
`;

const BackButton = styled.button`
    background: none;
    border: none;
    padding: 0;
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 500;
    color: #aaaaaa;
    cursor: pointer;
    align-self: flex-start;
    &:hover { color: #333333; }
`;

const TitleArea = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const PageBadge = styled.span`
    font-size: 11px;
    font-weight: 700;
    color: #487BFD;
    letter-spacing: 0.1em;
    text-transform: uppercase;
`;

const PageTitle = styled.h1`
    font-size: 24px;
    font-weight: 800;
    color: #111111;
    letter-spacing: -0.02em;
    margin: 0;
`;

const PageDesc = styled.p`
    font-size: 14px;
    color: #999999;
    line-height: 1.65;
    margin: 0;
`;

const TableSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0;
`;

const colBase = `
    font-family: Pretendard, -apple-system, sans-serif;
    font-size: 12px;
`;

const ColCategory = styled.div`
    ${colBase}
    width: 110px;
    flex-shrink: 0;
`;

const ColName = styled.div`
    ${colBase}
    flex: 1;
    min-width: 0;
`;

const ColPrice = styled.div`
    ${colBase}
    width: 100px;
    flex-shrink: 0;
    text-align: right;
`;

const ColDesc = styled.div`
    ${colBase}
    flex: 1.5;
    min-width: 0;
`;

const ColDuration = styled.div`
    ${colBase}
    width: 70px;
    flex-shrink: 0;
    text-align: right;
`;

const ColAction = styled.div`
    width: 40px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;
`;

const TableHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 0 20px 10px;
    font-size: 11px;
    font-weight: 600;
    color: #aaaaaa;
    letter-spacing: 0.04em;
`;

const ItemList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const ItemCard = styled.div`
    background: #ffffff;
    border: 1.5px solid ${({ $editing }) => $editing ? '#487BFD' : '#eeeeee'};
    border-radius: 14px;
    padding: ${({ $editing }) => $editing ? '20px' : '14px 20px'};
    cursor: ${({ $editing }) => $editing ? 'default' : 'pointer'};
    transition: border-color 0.15s, box-shadow 0.15s;
    box-shadow: ${({ $editing }) => $editing ? '0 0 0 4px rgba(72,123,253,0.08)' : 'none'};
    &:hover { border-color: ${({ $editing }) => $editing ? '#487BFD' : '#cccccc'}; }
`;

const PreviewRow = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const CategoryBadge = styled.span`
    display: inline-block;
    background: #f0f3ff;
    border: 1px solid #dce6ff;
    border-radius: 100px;
    padding: 3px 10px;
    font-size: 12px;
    font-weight: 500;
    color: #487BFD;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
`;

const EmptyCell = styled.span`
    font-size: 13px;
    color: #dddddd;
`;

const ServiceName = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: ${({ $empty }) => $empty ? '#cccccc' : '#111111'};
    font-style: ${({ $empty }) => $empty ? 'italic' : 'normal'};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: block;
`;

const PriceText = styled.span`
    font-size: 14px;
    font-weight: 700;
    color: ${({ $empty }) => $empty ? '#cccccc' : '#111111'};
    white-space: nowrap;
`;

const DescText = styled.span`
    font-size: 13px;
    color: ${({ $empty }) => $empty ? '#dddddd' : '#888888'};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: block;
`;

const DurationText = styled.span`
    font-size: 13px;
    color: ${({ $empty }) => $empty ? '#dddddd' : '#888888'};
    white-space: nowrap;
`;

const DeleteBtn = styled.button`
    background: none;
    border: none;
    font-size: 12px;
    color: #cccccc;
    cursor: pointer;
    padding: 2px 4px;
    line-height: 1;
    transition: color 0.15s;
    &:hover:not(:disabled) { color: #EF4444; }
    &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const UnsavedDot = styled.div`
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #f59e0b;
    flex-shrink: 0;
`;

/* Edit Form */
const EditForm = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
`;

const EditGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr;
    gap: 12px;

    @media (max-width: 640px) {
        grid-template-columns: 1fr 1fr;
    }
`;

const FormField = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const FieldLabel = styled.label`
    font-size: 12px;
    font-weight: 600;
    color: #aaaaaa;
`;

const Optional = styled.span`
    font-weight: 400;
`;

const inputBaseStyle = `
    width: 100%;
    box-sizing: border-box;
    background: #f7f8fa;
    border: 1px solid #e8e8e8;
    border-radius: 8px;
    padding: 9px 12px;
    font-family: Pretendard;
    font-size: 14px;
    color: #111111;
    outline: none;
    transition: border-color 0.15s;
    &::placeholder { color: #c0c0c0; }
    &:focus { border-color: #487BFD; background: #ffffff; }
`;

const FieldInput = styled.input`${inputBaseStyle}`;
const FieldTextarea = styled.textarea`
    ${inputBaseStyle}
    resize: none;
`;

const PriceInputWrap = styled.div`
    position: relative;
`;

const PriceUnit = styled.span`
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 13px;
    color: #aaaaaa;
    pointer-events: none;
`;

const DurationInputWrap = styled.div`
    position: relative;
`;

const DurationUnit = styled.span`
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 13px;
    color: #aaaaaa;
    pointer-events: none;
`;

const SaveError = styled.span`
    font-size: 12px;
    color: #EF4444;
`;

const SaveRow = styled.div`
    display: flex;
    gap: 8px;
    justify-content: flex-end;
`;

const CancelBtn = styled.button`
    background: none;
    border: 1px solid #e5e5e5;
    border-radius: 8px;
    padding: 7px 16px;
    font-family: Pretendard;
    font-size: 13px;
    font-weight: 500;
    color: #aaaaaa;
    cursor: pointer;
    transition: border-color 0.15s, color 0.15s;
    &:hover { border-color: #bbbbbb; color: #555555; }
`;

const SaveBtn = styled.button`
    background: #487BFD;
    border: none;
    border-radius: 8px;
    padding: 7px 20px;
    font-family: Pretendard;
    font-size: 13px;
    font-weight: 600;
    color: #ffffff;
    cursor: pointer;
    transition: opacity 0.15s;
    &:hover:not(:disabled) { opacity: 0.85; }
    &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const AddButton = styled.button`
    background: #ffffff;
    border: 1.5px dashed #d8d8d8;
    border-radius: 14px;
    padding: 16px;
    width: 100%;
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 500;
    color: #aaaaaa;
    cursor: pointer;
    transition: border-color 0.15s, color 0.15s, background 0.15s;
    &:hover { border-color: #487BFD; color: #487BFD; background: #f5f8ff; }
`;

const BottomArea = styled.div`
    padding-top: 8px;
    padding-bottom: 16px;
    display: flex;
    justify-content: center;
`;

const CountText = styled.p`
    font-size: 13px;
    color: #aaaaaa;
    margin: 0;
    text-align: center;
`;

const Strong = styled.strong`
    color: #487BFD;
    font-weight: 700;
`;

const StatusText = styled.span`
    font-family: Pretendard;
    font-size: 15px;
    color: ${({ $error }) => $error ? '#EF4444' : '#888888'};
`;


export default MenusPage;
